/**
 * Fonte única das cores da escala (SPEC-v3 §7; decisões 57, 59 e 60). LEGACY = cores de hoje;
 * PASTEL_V3 = S1 §5–§6. A escolha é pela flag VUE_APP_ENABLED_STATION_PALETTE_V3, que no build
 * vira constante — por isso os módulos podem avaliar `stationPalette()` na importação.
 */
export type CheckColorKey = 'ok' | 'ongoing' | 'late' | 'noout' | 'miss' | 'future'
export type StationTypeKey = 'FIXO' | 'COBERTURA' | 'EXTRA'
export type CardColors = { stripe: string; bg: string }
export type Swatch = CardColors & { text: string }
export type StationPalette = {
  id: 'legacy' | 'pastel-v3'
  type: Record<StationTypeKey, Swatch>
  offTeam: Swatch
  open: Swatch & { plus: string }
  alert: Swatch
  unidentified: Swatch
  checkIcon: Record<CheckColorKey, string>
  checkLens: Record<CheckColorKey, CardColors>
  teamLens: { ours: CardColors; others: CardColors }
  sectorDivider: string
  avatar: readonly string[]
  /** Nome da cor da vaga para textos de ajuda ("Vaga aberta fica sempre {openColorName}"). */
  openColorName: 'laranja' | 'verde'
}

export function tint(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16)
  return `rgba(${n >> 16},${(n >> 8) & 255},${n & 255},${alpha})`
}

const LEGACY_CHECK: Record<CheckColorKey, string> = {
  ok: '#198038',
  ongoing: '#6FDC8C',
  late: '#F1C21B',
  noout: '#3CA2B9',
  miss: '#DA1E28',
  future: '#C3C7D0'
}

// Paleta antiga: a lente Check pinta o fundo com 7% da cor da faixa.
const legacyCheckLens = Object.fromEntries(
  Object.entries(LEGACY_CHECK).map(([key, color]) => [
    key,
    { stripe: color, bg: tint(color, 0.07) }
  ])
) as Record<CheckColorKey, CardColors>

export const LEGACY_PALETTE: StationPalette = Object.freeze({
  id: 'legacy',
  type: {
    FIXO: { stripe: '#84C8D7', bg: '#F7FCFD', text: '#235E6C' },
    COBERTURA: { stripe: '#E37796', bg: '#FDF7F8', text: '#B4537A' },
    EXTRA: { stripe: '#198038', bg: '#F6FDF9', text: '#198038' }
  },
  offTeam: { stripe: '#BCBCCD', bg: '#F0F0F4', text: '#34344F' },
  open: { stripe: '#F18F1B', bg: '#FEF3E7', text: '#F18F1B', plus: '#F18F1B' },
  alert: { stripe: '#DA1E28', bg: '#DA1E28', text: '#FFFFFF' },
  unidentified: { stripe: '#68688D', bg: '#EEEEF3', text: '#34344F' },
  checkIcon: { ...LEGACY_CHECK },
  checkLens: legacyCheckLens,
  teamLens: {
    ours: { stripe: '#84C8D7', bg: '#F7FCFD' },
    others: { stripe: '#BCBCCD', bg: '#F0F0F4' }
  },
  sectorDivider: '#3CA2B9',
  avatar: ['#2F7F91', '#235E6C', '#3CA2B9', '#163D45', '#84C8D7', '#5E9AA8', '#4F7F8A', '#7D8794'],
  openColorName: 'laranja'
}) as StationPalette

export const PASTEL_V3_PALETTE: StationPalette = Object.freeze({
  id: 'pastel-v3',
  type: {
    // Textos escurecidos em relação à S1 §5 para AA (≥ 4,5:1 sobre o fundo e sobre branco).
    FIXO: { stripe: '#A9D8E3', bg: '#F7FCFD', text: '#3F6F7A' },
    COBERTURA: { stripe: '#EFB3C4', bg: '#FDF7F8', text: '#A94D72' },
    EXTRA: { stripe: '#C3B4E3', bg: '#F7F4FC', text: '#765DB4' }
  },
  offTeam: { stripe: '#CFCFDB', bg: '#F4F4F7', text: '#34344F' },
  open: { stripe: '#7CC79A', bg: '#EAF6EE', text: '#2E7D4F', plus: '#6CBF8C' },
  alert: { stripe: '#E8858B', bg: '#FDECEC', text: '#9B1C24' },
  unidentified: { stripe: '#A3A3BD', bg: '#F1F1F5', text: '#34344F' },
  checkIcon: {
    ok: '#3E9A5E',
    ongoing: '#7FCF98',
    late: '#E2B93B',
    noout: '#6FB3C4',
    miss: '#D9595F',
    future: '#C3C7D0'
  },
  checkLens: {
    ok: { stripe: '#7CC79A', bg: '#EAF6EE' },
    ongoing: { stripe: '#8FCBD8', bg: '#EAF5F8' },
    late: { stripe: '#EBCB6E', bg: '#FDF8E6' },
    noout: { stripe: '#B9A8DE', bg: '#F4F0FB' },
    miss: { stripe: '#E8949A', bg: '#FDEEEF' },
    future: { stripe: '#D3D5DE', bg: '#F6F7F9' }
  },
  teamLens: {
    ours: { stripe: '#8CC3CF', bg: '#EEF7F9' },
    others: { stripe: '#B9A8DE', bg: '#F4F0FB' }
  },
  sectorDivider: '#BFE0E8',
  avatar: ['#5E9AA8', '#4F7F8A', '#C98FA2', '#6FA585', '#9A88C4', '#C49A72', '#6FB3C4', '#7D8794'],
  openColorName: 'verde'
}) as StationPalette

/** Lido direto do env (e não do useFeatureFlags) para poder rodar na importação de módulos puros. */
export const isStationPaletteV3Enabled = (): boolean =>
  process.env.VUE_APP_ENABLED_STATION_PALETTE_V3 === 'true'

export function stationPalette(): StationPalette {
  return isStationPaletteV3Enabled() ? PASTEL_V3_PALETTE : LEGACY_PALETTE
}

export const STATION_PALETTE_CSS_VARS = [
  '--st-open-stripe',
  '--st-open-bg',
  '--st-open-text',
  '--st-open-tint',
  '--st-open-ring',
  '--st-open-tint-tv',
  '--st-open-tv-bg'
] as const

export function stationPaletteCssVars(
  p: StationPalette = stationPalette()
): Record<string, string> {
  return {
    '--st-open-stripe': p.open.stripe,
    '--st-open-bg': p.open.bg,
    '--st-open-text': p.open.text,
    // Pulso/realce da vaga (UnitMapSlot): mesma cor da faixa com alfa; no legado = laranja de hoje.
    '--st-open-tint': tint(p.open.stripe, 0.25),
    '--st-open-ring': tint(p.open.stripe, 0.95),
    '--st-open-tint-tv': tint(p.open.stripe, 0.45),
    // Fundo do slot de vaga no Modo TV do mapa (fundo escuro): legado = marrom-laranja de hoje.
    '--st-open-tv-bg': p.id === 'legacy' ? '#3A2A16' : '#1E3A2B'
  }
}

/** Só chamar com a flag ligada: desligada, os `var(--st-open-*, <legado>)` caem no fallback de hoje. */
export function applyStationPaletteCssVars(
  el: HTMLElement,
  p: StationPalette = stationPalette()
): void {
  Object.entries(stationPaletteCssVars(p)).forEach(([key, value]) =>
    el.style.setProperty(key, value)
  )
}
